import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Divider,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Badge,
  Snackbar,
  CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Upload as UploadIcon,
  Link as LinkIcon,
  Description as DocumentIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Storage as StorageIcon,
  SmartToy as BotIcon,
  Business as BusinessIcon,
  School as KnowledgeIcon,
  Assignment as AssignmentIcon,
  CloudUpload as CloudUploadIcon,
  FileCopy as FileCopyIcon,
  Psychology as PsychologyIcon
} from '@mui/icons-material';
import axios from '../../utils/axiosConfig';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import JobStatusTracker from '../../components/JobStatusTracker';

const KnowledgeBase = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [jobCompleteNotification, setJobCompleteNotification] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Knowledge Base Data
  const [knowledgeItems, setKnowledgeItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Bot Assignment Data
  const [bots, setBots] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [botAssignments, setBotAssignments] = useState({});
  
  // Dialog States
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [selectedKnowledgeItem, setSelectedKnowledgeItem] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  
  // Upload Form
  const [uploadForm, setUploadForm] = useState({
    title: '',
    content: '',
    category: 'general',
    file: null,
    url: '',
    type: 'text',
    selectedBots: [],
    shopifyToken: '',
    shopifyTenantId: '',
  });

  useEffect(() => {
    loadKnowledgeItems();
    loadBots();
    loadTenants();
    loadCompanies();
    loadBotAssignments();
  }, []);

  const loadTenants = async () => {
    try {
      const response = await axios.get('/api/tenants/');
      setTenants(response.data || []);
    } catch (error) {
      console.warn('Tenants optional:', error);
    }
  };

  const loadKnowledgeItems = async () => {
    try {
      setLoading(true);
      console.log('Lade Wissensdatenbank...');
      const response = await axios.get(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/knowledge/`);
      console.log('Wissensdatenbank Response:', response.data);
      setKnowledgeItems(response.data || []);
    } catch (error) {
      console.error('Fehler beim Laden der Wissensdatenbank:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Fehler beim Laden der Wissensdatenbank';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadBots = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/bots/`);
      setBots(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Bots:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Fehler beim Laden der Bots';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  const loadCompanies = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/companies/`);
      setCompanies(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Firmen:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Fehler beim Laden der Firmen';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  const loadBotAssignments = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/knowledge/bot-assignments/`);
      const assignments = {};
      response.data.forEach(assignment => {
        if (!assignments[assignment.knowledge_id]) {
          assignments[assignment.knowledge_id] = [];
        }
        assignments[assignment.knowledge_id].push(assignment.bot_id);
      });
      setBotAssignments(assignments);
    } catch (error) {
      console.error('Fehler beim Laden der Bot-Zuweisungen:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Fehler beim Laden der Bot-Zuweisungen';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  const handleUpload = async () => {
    try {
      setSaving(true);
      const formData = new FormData();
      formData.append('title', uploadForm.title);
      formData.append('content', uploadForm.content);
      formData.append('category', uploadForm.category);
      formData.append('type', uploadForm.type);
      
      if (uploadForm.file) {
        formData.append('file', uploadForm.file);
      }
      if (uploadForm.url) {
        formData.append('url', uploadForm.url);
      }
      
      // Shopify Token hinzufügen
      if (uploadForm.shopifyToken) {
        formData.append('shopify_access_token', uploadForm.shopifyToken);
      }
      if (uploadForm.shopifyTenantId) {
        formData.append('shopify_tenant_id', String(uploadForm.shopifyTenantId));
      }
      
      // Bot-Zuweisungen hinzufügen
      if (uploadForm.selectedBots && uploadForm.selectedBots.length > 0) {
        formData.append('bot_ids', JSON.stringify(uploadForm.selectedBots));
      }

      // Spezielle Behandlung für Shopify-URLs
      if (uploadForm.type === 'shopify_url') {
        setSnackbar({ 
          open: true, 
          message: 'Shopify Storefront API wird abgefragt... Lade Produkte, Sammlungen, Blogs und Seiten.', 
          severity: 'info' 
        });
      }

      // Wähle Endpoint basierend auf Upload-Typ
      const endpoint = uploadForm.type === 'shopify_url' 
        ? '/api/knowledge/upload-async/'  // Asynchron für Shopify-URLs
        : '/api/knowledge/upload/';       // Normal für andere Uploads

      const response = await axios.post(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}${endpoint}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000, // 5 Minuten Timeout für UI
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      // Prüfe ob asynchroner Job gestartet wurde
      if (response.data.status === 'processing') {
        setSnackbar({ 
          open: true, 
          message: 'Crawling-Job gestartet! Die Website wird im Hintergrund verarbeitet und PDF-Dateien werden automatisch erkannt und verarbeitet.', 
          severity: 'info' 
        });
        setUploadDialogOpen(false);
        resetUploadForm();
      } else {
        // Normale Verarbeitung abgeschlossen
        setKnowledgeItems(prev => [response.data, ...prev]);
        setUploadDialogOpen(false);
        resetUploadForm();
        
        const successMessage = uploadForm.type === 'shopify_url' 
          ? 'Shopify Storefront API erfolgreich abgefragt und als Wissensdatenbank gespeichert!' 
          : 'Wissensdatenbank erfolgreich hochgeladen!';
        
        setSnackbar({ open: true, message: successMessage, severity: 'success' });
      }
    } catch (error) {
      console.error('Fehler beim Hochladen:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Fehler beim Hochladen der Wissensdatenbank';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setSaving(false);
      setUploadProgress(0);
    }
  };

  const handleDeleteKnowledge = async (id) => {
    if (window.confirm('Möchten Sie diesen Eintrag wirklich löschen?')) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/knowledge/${id}`);
        setKnowledgeItems(prev => prev.filter(item => item.id !== id));
        setSnackbar({ open: true, message: 'Eintrag erfolgreich gelöscht!', severity: 'success' });
      } catch (error) {
        console.error('Fehler beim Löschen:', error);
        const errorMessage = error.response?.data?.detail || error.message || 'Fehler beim Löschen';
        setSnackbar({ open: true, message: errorMessage, severity: 'error' });
      }
    }
  };

  const handleAssignToBots = (knowledgeItem) => {
    setSelectedKnowledgeItem(knowledgeItem);
    setAssignmentDialogOpen(true);
  };

  const handleSaveBotAssignments = async () => {
    try {
      setSaving(true);
      const assignments = botAssignments[selectedKnowledgeItem.id] || [];
      
      await axios.post(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/knowledge/bot-assignments/`, {
        knowledge_id: selectedKnowledgeItem.id,
        bot_ids: assignments
      });

      setAssignmentDialogOpen(false);
      setSnackbar({ open: true, message: 'Bot-Zuweisungen erfolgreich gespeichert!', severity: 'success' });
    } catch (error) {
      console.error('Fehler beim Speichern der Bot-Zuweisungen:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Fehler beim Speichern der Bot-Zuweisungen';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleBotAssignmentChange = (botId, checked) => {
    const knowledgeId = selectedKnowledgeItem.id;
    setBotAssignments(prev => ({
      ...prev,
      [knowledgeId]: checked 
        ? [...(prev[knowledgeId] || []), botId]
        : (prev[knowledgeId] || []).filter(id => id !== botId)
    }));
  };

  const resetUploadForm = () => {
    setUploadForm({
      title: '',
      content: '',
      category: 'general',
      file: null,
      url: '',
      type: 'text',
      selectedBots: [],
      shopifyToken: '',
      shopifyTenantId: '',
    });
  };

  const handleResyncShopify = async (item) => {
    const tid = window.prompt(
      'Optional: Mandanten-ID (Zahl), wenn der Storefront-Token als ApiCredential hinterlegt ist und nicht im Eintrag:',
      ''
    );
    try {
      setSaving(true);
      const params = {};
      if (tid && String(tid).trim()) params.shopify_tenant_id = parseInt(String(tid).trim(), 10);
      await axios.post(`/api/knowledge/${item.id}/resync`, null, { params });
      setSnackbar({ open: true, message: 'Shopify Re-Sync abgeschlossen.', severity: 'success' });
      loadKnowledgeItems();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Re-Sync fehlgeschlagen';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : 'Unbekannt';
  };

  const getBotName = (botId) => {
    const bot = bots.find(b => b.id === botId);
    return bot ? bot.name : 'Unbekannt';
  };

  const filteredKnowledgeItems = knowledgeItems.filter(item => {
    const title = item.name || item.title || '';
    const content = item.description || item.content || '';
    const category = item.category || 'general';
    
    const matchesSearch = title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         content.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getCategoryColor = (category) => {
    const colors = {
      general: 'default',
      technical: 'primary',
      faq: 'secondary',
      product: 'success',
      support: 'warning',
      legal: 'error'
    };
    return colors[category] || 'default';
  };

  const handleJobComplete = (job) => {
    setJobCompleteNotification({
      open: true,
      message: `Job "${job.title}" wurde erfolgreich abgeschlossen!`,
      severity: 'success'
    });
    // Lade Knowledge Base neu
    loadKnowledgeItems();
  };

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>
          Wissensdatenbank
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Verwalten Sie die Wissensbasis und weisen Sie sie Bots zu
        </Typography>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setUploadDialogOpen(true)}
          sx={{
            background: 'linear-gradient(135deg, #FFD700 0%, #F57F17 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #F57F17 0%, #FFD700 100%)',
            }
          }}
        >
          Neuer Eintrag
        </Button>
        
        {/* Direkter Datei-Upload */}
        <Button
          variant="outlined"
          component="label"
          startIcon={<CloudUploadIcon />}
          sx={{ borderStyle: 'dashed' }}
        >
          Datei hochladen
          <input
            type="file"
            hidden
            accept=".pdf,.txt,.doc,.docx,.md"
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                // Automatisch den Dateinamen als Titel übernehmen
                const fileName = file.name.replace(/\.[^/.]+$/, ""); // Entferne Dateiendung
                setUploadForm({ 
                  title: fileName,
                  content: '',
                  category: 'general',
                  file: file,
                  url: '',
                  type: 'file',
                  selectedBots: []
                });
                setUploadDialogOpen(true);
              }
            }}
          />
        </Button>
        
      </Box>

      {/* Job Status Tracker */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <JobStatusTracker onJobComplete={handleJobComplete} />
        </CardContent>
      </Card>

      {/* Search and Filter */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Wissensdatenbank durchsuchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Kategorie</InputLabel>
                <Select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  label="Kategorie"
                >
                  <MenuItem value="all">Alle Kategorien</MenuItem>
                  <MenuItem value="general">Allgemein</MenuItem>
                  <MenuItem value="technical">Technisch</MenuItem>
                  <MenuItem value="faq">FAQ</MenuItem>
                  <MenuItem value="product">Produkt</MenuItem>
                  <MenuItem value="support">Support</MenuItem>
                  <MenuItem value="legal">Rechtlich</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary">
                {filteredKnowledgeItems.length} Einträge gefunden
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Knowledge Items Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <KnowledgeIcon />
            Wissensdatenbank ({filteredKnowledgeItems.length})
          </Typography>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Titel</TableCell>
                    <TableCell>Kategorie</TableCell>
                    <TableCell>Typ</TableCell>
                    <TableCell>Zugewiesene Bots</TableCell>
                    <TableCell>Erstellt</TableCell>
                    <TableCell>Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredKnowledgeItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DocumentIcon color="primary" />
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {item.name || item.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ 
                              maxWidth: 300,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {item.description || (item.content ? item.content.substring(0, 100) + '...' : 'Keine Beschreibung')}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={item.category || 'general'} 
                          color={getCategoryColor(item.category || 'general')}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={item.source_type || item.type || 'text'} 
                          color={item.source_type === 'shopify_url' ? 'secondary' : 'primary'} 
                          variant="outlined"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(botAssignments[item.id] || []).map(botId => {
                            const bot = bots.find(b => b.id === botId);
                            return (
                              <Chip
                                key={botId}
                                label={bot?.name || 'Unbekannt'}
                                size="small"
                                color="success"
                                icon={<BotIcon />}
                              />
                            );
                          })}
                          {(botAssignments[item.id] || []).length === 0 && (
                            <Typography variant="caption" color="text.secondary">
                              Keine Zuweisungen
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {item.created_at ? format(new Date(item.created_at), 'dd.MM.yyyy HH:mm', { locale: de }) : 'Unbekannt'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Bots zuweisen">
                            <IconButton
                              onClick={() => handleAssignToBots(item)}
                              size="small"
                              color="primary"
                            >
                              <AssignmentIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Anzeigen">
                            <IconButton
                              onClick={() => {
                                setSelectedKnowledgeItem(item);
                                setViewDialogOpen(true);
                              }}
                              size="small"
                              color="primary"
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          {item.source_type === 'shopify_url' && (
                            <Tooltip title="Shopify neu einlesen (Re-Sync)">
                              <IconButton
                                onClick={() => handleResyncShopify(item)}
                                size="small"
                                color="secondary"
                              >
                                <RefreshIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Löschen">
                            <IconButton
                              onClick={() => handleDeleteKnowledge(item.id)}
                              size="small"
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CloudUploadIcon />
            Wissensdatenbank hochladen
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Titel"
                value={uploadForm.title}
                onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Typ</InputLabel>
                <Select
                  value={uploadForm.type}
                  onChange={(e) => setUploadForm({ ...uploadForm, type: e.target.value })}
                  label="Typ"
                >
                  <MenuItem value="text">Text</MenuItem>
                  <MenuItem value="file">Datei</MenuItem>
                  <MenuItem value="url">URL</MenuItem>
                  <MenuItem value="shopify_url">Shopify-Website</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Kategorie</InputLabel>
                <Select
                  value={uploadForm.category}
                  onChange={(e) => setUploadForm({ ...uploadForm, category: e.target.value })}
                  label="Kategorie"
                >
                  <MenuItem value="general">Allgemein</MenuItem>
                  <MenuItem value="technical">Technisch</MenuItem>
                  <MenuItem value="faq">FAQ</MenuItem>
                  <MenuItem value="product">Produkt</MenuItem>
                  <MenuItem value="support">Support</MenuItem>
                  <MenuItem value="legal">Rechtlich</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {uploadForm.type === 'text' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  label="Inhalt"
                  value={uploadForm.content}
                  onChange={(e) => setUploadForm({ ...uploadForm, content: e.target.value })}
                  placeholder="Geben Sie den Inhalt der Wissensdatenbank ein..."
                />
              </Grid>
            )}
            {uploadForm.type === 'file' && (
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadIcon />}
                  fullWidth
                  sx={{ py: 2 }}
                >
                  Datei auswählen
                  <input
                    type="file"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        // Automatisch den Dateinamen als Titel übernehmen
                        const fileName = file.name.replace(/\.[^/.]+$/, ""); // Entferne Dateiendung
                        setUploadForm({ 
                          ...uploadForm, 
                          file: file,
                          title: uploadForm.title || fileName // Nur setzen wenn noch kein Titel vorhanden
                        });
                      }
                    }}
                    accept=".pdf,.txt,.doc,.docx,.md"
                  />
                </Button>
                {uploadForm.file && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Ausgewählte Datei: {uploadForm.file.name}
                    </Typography>
                    <Alert severity="success" sx={{ mt: 1 }}>
                      <Typography variant="body2">
                        <strong>Automatisch übernommen:</strong> Der Dateiname wurde als Titel verwendet. 
                        Sie können den Titel bei Bedarf anpassen.
                      </Typography>
                    </Alert>
                  </Box>
                )}
              </Grid>
            )}
            {uploadForm.type === 'url' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="URL"
                  value={uploadForm.url}
                  onChange={(e) => setUploadForm({ ...uploadForm, url: e.target.value })}
                  placeholder="https://example.com/document"
                />
              </Grid>
            )}
            {uploadForm.type === 'shopify_url' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Shopify-Website URL"
                  value={uploadForm.url}
                  onChange={(e) => setUploadForm({ ...uploadForm, url: e.target.value })}
                  placeholder="https://your-shop.myshopify.com"
                  helperText="Geben Sie die URL Ihrer Shopify-Website ein."
                />
                <TextField
                  fullWidth
                  label="Shopify Storefront Access Token (optional)"
                  value={uploadForm.shopifyToken || ''}
                  onChange={(e) => setUploadForm({ ...uploadForm, shopifyToken: e.target.value })}
                  placeholder="48f5b7aa9805e3a9b59f8d1bd7595af7"
                  helperText="Lassen Sie leer für Standard-Token oder geben Sie Ihren eigenen Storefront Access Token ein."
                  sx={{ mt: 2 }}
                />
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel id="shopify-tenant-label">Mandant (optional, Credential shopify_storefront)</InputLabel>
                  <Select
                    labelId="shopify-tenant-label"
                    label="Mandant (optional, Credential shopify_storefront)"
                    value={uploadForm.shopifyTenantId === '' ? '' : uploadForm.shopifyTenantId}
                    onChange={(e) =>
                      setUploadForm({ ...uploadForm, shopifyTenantId: e.target.value === '' ? '' : e.target.value })
                    }
                  >
                    <MenuItem value="">
                      <em>Kein Mandant</em>
                    </MenuItem>
                    {tenants.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.name} ({t.slug})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Shopify Storefront API:</strong> Das System verwendet die offizielle Shopify Storefront API, 
                    um strukturierte Daten zu laden. Dies umfasst:
                    <br />• <strong>Produkte:</strong> Titel, Beschreibung, Preise, Varianten, Bilder, Tags
                    <br />• <strong>Sammlungen:</strong> Kategorien und Produktgruppen
                    <br />• <strong>Blog-Artikel:</strong> Alle Blog-Posts mit Inhalten und Tags
                    <br />• <strong>Seiten:</strong> Über uns, FAQ, Versand, etc.
                    <br />Die API liefert zuverlässige, strukturierte Daten ohne Web-Scraping.
                  </Typography>
                </Alert>
              </Grid>
            )}
            {uploadProgress > 0 && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LinearProgress variant="determinate" value={uploadProgress} sx={{ flex: 1 }} />
                  <Typography variant="body2">{uploadProgress}%</Typography>
                </Box>
              </Grid>
            )}
            
            {/* Bot-Auswahl */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Bots zuweisen (optional)</InputLabel>
                <Select
                  multiple
                  value={uploadForm.selectedBots || []}
                  onChange={(e) => setUploadForm({ ...uploadForm, selectedBots: e.target.value })}
                  label="Bots zuweisen (optional)"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((botId) => {
                        const bot = bots.find(b => b.id === botId);
                        return (
                          <Chip
                            key={botId}
                            label={bot?.name || 'Unbekannt'}
                            size="small"
                            color="primary"
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {bots.map((bot) => (
                    <MenuItem key={bot.id} value={bot.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BotIcon />
                        <Box>
                          <Typography variant="body2">{bot.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {getCompanyName(bot.company_id)}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>
            Abbrechen
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={saving || !uploadForm.title || (!uploadForm.content && !uploadForm.file && !uploadForm.url)}
            startIcon={saving ? <CircularProgress size={20} /> : <CloudUploadIcon />}
          >
            {saving ? (uploadForm.type === 'shopify_url' ? 'Lade API-Daten...' : 'Lädt hoch...') : 'Hochladen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bot Assignment Dialog */}
      <Dialog
        open={assignmentDialogOpen}
        onClose={() => setAssignmentDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssignmentIcon />
            Bots zuweisen: {selectedKnowledgeItem?.title}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Wählen Sie die Bots aus, die Zugriff auf diese Wissensdatenbank haben sollen.
          </Typography>
          
          <List>
            {bots.map((bot) => (
              <ListItem key={bot.id}>
                <ListItemIcon>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <BotIcon />
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={bot.name}
                  secondary={`${getCompanyName(bot.company_id)} • ${bot.description || 'Keine Beschreibung'}`}
                />
                <ListItemSecondaryAction>
                  <Checkbox
                    checked={(botAssignments[selectedKnowledgeItem?.id] || []).includes(bot.id)}
                    onChange={(e) => handleBotAssignmentChange(bot.id, e.target.checked)}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignmentDialogOpen(false)}>
            Abbrechen
          </Button>
          <Button
            onClick={handleSaveBotAssignments}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <AssignmentIcon />}
          >
            {saving ? 'Speichere...' : 'Zuweisungen speichern'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Knowledge Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ViewIcon />
            {selectedKnowledgeItem?.name || 'Wissensdatenbank anzeigen'}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedKnowledgeItem && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedKnowledgeItem.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {selectedKnowledgeItem.description}
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Chip 
                  label={selectedKnowledgeItem.source_type || 'text'} 
                  color="primary" 
                  size="small"
                  sx={{ mr: 1 }}
                />
                <Chip 
                  label={selectedKnowledgeItem.category || 'general'} 
                  color="secondary" 
                  size="small"
                />
              </Box>
              
              <Typography variant="subtitle2" gutterBottom>
                Inhalt:
              </Typography>
              <Box 
                sx={{ 
                  maxHeight: 400, 
                  overflow: 'auto', 
                  p: 2, 
                  backgroundColor: 'grey.50', 
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'grey.300'
                }}
              >
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedKnowledgeItem.source_config?.content || 
                   selectedKnowledgeItem.content || 
                   'Kein Inhalt verfügbar'}
                </Typography>
              </Box>
              
              {/* Scraping-Statistiken */}
              {selectedKnowledgeItem.source_type === 'shopify_url' && selectedKnowledgeItem.source_config && (
                <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Scraping-Statistiken:
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Produkte:</strong> {selectedKnowledgeItem.source_config.total_products || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Sammlungen:</strong> {selectedKnowledgeItem.source_config.total_collections || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Blogs:</strong> {selectedKnowledgeItem.source_config.total_blogs || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Seiten:</strong> {selectedKnowledgeItem.source_config.total_pages || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Website-Seiten:</strong> {selectedKnowledgeItem.source_config.total_website_pages || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Erfolgreich gescraped:</strong> {selectedKnowledgeItem.source_config.successfully_scraped_website || 0}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {selectedKnowledgeItem.source_config?.scraped_pages && selectedKnowledgeItem.source_config.scraped_pages.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Gescrapte Seiten ({selectedKnowledgeItem.source_config.scraped_pages.length}):
                  </Typography>
                  <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                    {selectedKnowledgeItem.source_config.scraped_pages.slice(0, 10).map((page, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <LinkIcon />
                        </ListItemIcon>
                        <ListItemText 
                          primary={page}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                    {selectedKnowledgeItem.source_config.scraped_pages.length > 10 && (
                      <ListItem>
                        <ListItemText 
                          primary={`... und ${selectedKnowledgeItem.source_config.scraped_pages.length - 10} weitere Seiten`}
                          primaryTypographyProps={{ variant: 'body2', fontStyle: 'italic' }}
                        />
                      </ListItem>
                    )}
                  </List>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />

      {/* Job Complete Notification */}
      {jobCompleteNotification && (
        <Snackbar
          open={jobCompleteNotification.open}
          autoHideDuration={8000}
          onClose={() => setJobCompleteNotification(null)}
        >
          <Alert 
            onClose={() => setJobCompleteNotification(null)} 
            severity={jobCompleteNotification.severity}
            sx={{ width: '100%' }}
          >
            {jobCompleteNotification.message}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
};

export default KnowledgeBase;